<template>
  <div class="datatable">
    <DataGrid   :id="props.controller + props.object + 'ListGrid'"
                keyExpr="id"
                :data-source="dataSource"
                :onRowClick="openItem"
                selectionMode="single"
                :hoverStateEnabled="true"
                :contentReadyAction="onContentReady"
                :toolbarOptions="toolbar"
                :allowExport="props.allowExport"
                :exportData="exportData"
                :gridProperties="props.gridProperties">
      <DxRemoteOperations :paging="true" :sorting="true" :filtering="true" />
      <DxFilterRow :visible="true" :show-operation-chooser="true"/>
      <slot />
    </DataGrid>
  </div>
</template>

<script setup>
import { onMounted, ref, watchEffect, onBeforeMount } from "vue";
import router from "@/router";
import { DxFilterRow, DxPager, DxPaging, DxHeaderFilter, DxRemoteOperations } from "devextreme-vue/cjs/data-grid";
import { CustomStore } from "devextreme/common/data";
import DataGrid from "../datagrid/DataGrid.vue";

import { useLocalization } from "@/locales/localization";
const localization = useLocalization();

import { useAuthStore } from "@/stores/auth";
const auth = useAuthStore();

import { useApiClient } from "@/utils/apiClient.js";
const api = useApiClient();

var lastLoadOptions = ref(null);

const props = defineProps({
  controller: { type: String },
  object: { type: String },
  requestProperties: { type: Array },
  gridProperties: { type: Object },
  customView: { type: String },
  title: { type: String },
  toolbarOptions: { type: Array, default: () => [] },
  allowExport: { type: Boolean, default: false },
  processItems: { type: Object }
});
const isBusy = ref(true);
const isInitialized = ref(false);
const totalCount = ref(0);
const params = ref({
  page: 1,
  pageSize: 10,
  sortColumn: "id",
  sortDirection: "asc",
  columnFilters: [],
});

const toolbar = ref([
  (auth.hasPermission(props.object + "set") ? {
    location: 'after',
    widget: 'dxButton',
    options: {
      icon: 'add',
      hint: localization.t('common.actions.add'),
      onClick: () => newItem()
    }
  } : undefined),
  {
    location: 'before',
    template() {
      const label = document.createElement('div');
      label.textContent = (props.title ? props.title : localization.t(props.controller + '.' + props.object + '.title'));
      label.classList.add('dx-form-group-caption');
      return label;
    }
  },
  {
    location: 'before',
    template() {
      const label = document.createElement('div');
      watchEffect(() => {
        label.textContent = localization.t('common.labels.count') + ': ' + totalCount.value;
      });
      label.style.marginLeft = '40px';
      return label;
    }
  }
].filter(Boolean));

async function exportData(){
  var loadOptions = {...lastLoadOptions.value};
  loadOptions.skip = 0;
  loadOptions.take = 0;
  var result = await dataSource.load(loadOptions);
  return result.data;
}

const items = ref([]);
const dataSource = new CustomStore({
    key: props.object.value,
    load: async (loadOptions) => {
      lastLoadOptions.value = {...loadOptions};
      var take = (loadOptions.take !== null && loadOptions.take > 0 ? loadOptions.take : 1);
      var page = Math.floor(loadOptions.skip / take);
      params.value.page = page + 1;
      params.value.pageSize = loadOptions.take;

      if (loadOptions.sort && loadOptions.sort.length > 0) {
        params.value.sortColumn = loadOptions.sort[0].selector;
        params.value.sortDirection = loadOptions.sort[0].desc === true ? 'desc' : 'asc';
      }

      if (loadOptions.filter) {
        var filters = [];

        // The Devex filter options are passed weirdly. If only one column has a filter text, the filter
        // data is passed in the filter property directly. If multiple columns have filter text, the filter
        // property contains an array of filter datas
        // So we check if a filter data property is present, which means only one column, otherwise loop
        if (loadOptions.filter.columnIndex !== undefined) {
          filters.push(createRequestFilter(loadOptions.filter));
        } else {
          for (var i = 0; i < loadOptions.filter.length; i++) {
            if (Array.isArray(loadOptions.filter[i])) {
              filters.push(createRequestFilter(loadOptions.filter[i]));
            }
          }
        }

        params.value.columnFilters = filters;
      } else {
        params.value.columnFilters = [];
      }

      await load();
      return {
          data: items.value,
          totalCount: totalCount.value,
      };
    },
});

function createRequestFilter(filter) {
  if(!Array.isArray(filter)) return null;
  var f = {};

  // Check if filter type is between to add support for from - to filters
  if(Array.isArray(filter[0]) && filter[1] === "and" && Array.isArray(filter[2])){
    f = {
      filter: true,
      field: filter[0][0],
      operator: "between",
      value: [filter.filterValue[0], filter.filterValue[1]]
    };
  }
  else{
    var filterValue = filter.filterValue;
    if (filterValue === true) filterValue = 'true';
    else if (filterValue === false) filterValue = 'false';
    
    var field = filter[0];
    var operator = filter[1];
    if(operator === ">=") field += "From";
    if(operator === "<=") field += "To";

    f = {
      filter: true,
      field: field,
      operator: operator,
      value: filterValue,
    };
  }
  return f;
}

onMounted(() => {
  load();
});

async function load() {
  isBusy.value = true;

  var filters = [];
  for (var i = 0; i < params.value.columnFilters.length; i++) {
    var filter = params.value.columnFilters[i];
    if (filter.filter === true) {
      if(filter.operator === "between"){
        var dateFrom = filter.value[0];
        var dateTo = filter.value[1];
        if(filter.value[0] && filter.value[1]){
          filters.push(
            {
              columnName: filter.field + "From",
              filterValue: dateFrom
            },
            {
              columnName: filter.field + "To",
              filterValue: dateTo
            }
          );
        }
      }else{
        if(filter.value){
          filters.push({
            columnName: filter.field,
            filterValue: filter.value
          });
        }
      }
    }
  }

  // Convert to UTC datetime
  filters.forEach(o => {
    if(o.filterValue instanceof Date){
      o.filterValue = new Date(Date.UTC(
        o.filterValue.getFullYear(),
        o.filterValue.getMonth(),
        o.filterValue.getDate(),
        o.filterValue.getHours(),
        o.filterValue.getMinutes(),
        o.filterValue.getSeconds(),
        o.filterValue.getMilliseconds()
      ));
    }
  });


  var request = {
    page: params.value.page,
    pageSize: params.value.pageSize,
    sortColumn: params.value.sortColumn,
    sortDirection: params.value.sortDirection,
    filters: filters,
  };

  if (props.requestProperties) {
    props.requestProperties.forEach(o => {
      request[o.key] = o.value;
    });
  }

  var url = "/" + props.controller + "/" + props.object + "list";
  var result = await api.post(url, request);

  if (result.ok !== true) {
    isBusy.value = false;
    return;
  }
  var data = result.data.rows;
  if(props.processItems !== null && props.processItems !== undefined){
    data = props.processItems(data);
  }

  items.value = data;
  totalCount.value = result.data.totalCount;

  isBusy.value = false;
  isInitialized.value = true;
}

function openItem(data) {
  var view = props.object;
  if(props.customView) view = props.customView;
  router.push("/" + props.controller + "/" + view + "/" + data.key.id);
}

function newItem() {
  var view = props.object;
  if(props.customView) view = props.customView;
  router.push("/" + props.controller + "/" + view);
}

function onContentReady(o){
  o.component.getVisibleColumns().forEach(col => {
    if(col.dataType === "date" || col.dataType === "datetime"){
      o.component.columnOption(col.dataField, "filterOperations", ["between"]);
      o.component.columnOption(col.dataField, "selectedFilterOperation", "between");
    }
    if(col.dataType === "string"){
      o.component.columnOption(col.dataField, "filterOperations", ["contains"]);
    }
  });
}

onBeforeMount(() => {
  toolbar.value = [...toolbar.value, ...props.toolbarOptions];
});
</script>

<style>
.dx-data-row.dx-state-hover > td {  
  cursor: pointer;  
}  
</style>
